// Seeds the Historical Graph dialog's start/end window from the right-click
// position on a trend plot, mirroring the C++ replay-from-trend logic in
// graph_dialog.cpp (~line 1905): a window around the cursor time sized by the
// selected plot's sample rate.

/**
 * Estimate the trend sample rate (seconds between samples) from a series'
 * timestamps. Uses the median of consecutive positive deltas so data gaps don't
 * skew it. Returns null when there aren't enough points to tell.
 */
export function estimateSampleRateSecs(timestamps: number[] | undefined): number | null {
  if (!timestamps || timestamps.length < 2) return null;
  const deltas: number[] = [];
  for (let i = 1; i < timestamps.length; i++) {
    const d = timestamps[i] - timestamps[i - 1];
    if (d > 0) deltas.push(d);
  }
  if (deltas.length === 0) return null;
  deltas.sort((a, b) => a - b);
  const mid = Math.floor(deltas.length / 2);
  const median = deltas.length % 2 ? deltas[mid] : (deltas[mid - 1] + deltas[mid]) / 2;
  return Math.round(median);
}

/**
 * The [start, end] window (in the same units as `cursorSecs`) to seed the
 * Historical Graph dialog with, given the cursor time and the plot's sample
 * rate. Matches graph_dialog.cpp:
 *   sampleRate ≤ 3600 → start = cursor − 0.5·sr,  end = start + 2·sr
 *   sampleRate  > 3600 → start = cursor,          end = cursor + sr − 1
 * Bounds are rounded to whole seconds.
 */
export function historicalGraphWindow(
  cursorSecs: number,
  sampleRateSecs: number,
): { start: number; end: number } {
  if (sampleRateSecs <= 3600) {
    const start = Math.round(cursorSecs - 0.5 * sampleRateSecs);
    return { start, end: start + 2 * sampleRateSecs };
  }
  const start = Math.round(cursorSecs);
  return { start, end: start + sampleRateSecs - 1 };
}

/** One plot as the seeding helpers below see it: what its reply reported, its
 *  own sample times/values, and whether it is currently drawn. */
export interface PlotSampleInfo {
  /** Sample rate reported by the plot's own graph reply, when it carried one. */
  sampleRate?: number | null;
  /** The plot's own sample times (same units as the cursor). */
  timestamps?: number[];
  /** The plot's own sample values, index-aligned with `timestamps`. */
  values?: (number | null)[];
  /** False when the legend has the plot hidden. Absent counts as visible. */
  visible?: boolean;
}

/** Index of the sample in `timestamps` nearest `cursorSecs`, or null when the
 *  plot has no samples. Assumes ascending timestamps (linear scan — a trend
 *  plot holds a few thousand points at most). */
function nearestSampleIndex(timestamps: number[] | undefined, cursorSecs: number): number | null {
  if (!timestamps || timestamps.length === 0) return null;
  let best = 0;
  let bestDist = Math.abs(timestamps[0] - cursorSecs);
  for (let i = 1; i < timestamps.length; i++) {
    const d = Math.abs(timestamps[i] - cursorSecs);
    if (d < bestDist) {
      best = i;
      bestDist = d;
    }
  }
  return best;
}

/**
 * The plot's own sample time nearest the cursor, or null when it has no samples.
 *
 * The C++ trend menu anchors its replay/historical window on
 * `my_replay_plot_time` (graph_dialog.cpp:12514) — the x value of the data point
 * closest to the cursor, taken from the plot closest to the cursor — and only
 * falls back to the raw `my_cursor_time` when that is unset. Seeding from the
 * raw pixel time instead skews the window by up to half a sample period, which
 * on an hourly trend is half an hour.
 */
export function snapToPlotSample(timestamps: number[] | undefined, cursorSecs: number): number | null {
  const i = nearestSampleIndex(timestamps, cursorSecs);
  return i == null ? null : timestamps![i];
}

/** A positive rate, or null. */
function positiveRate(rate: number | null | undefined): number | null {
  return rate != null && rate > 0 ? rate : null;
}

/**
 * Sample rate (seconds) to size the window with, for the plot the C++ menu would
 * read: `((tiger_time_plot *)selected_plot_ptr)->get_plot_samplerate()`
 * (graph_dialog.cpp:1905), i.e. the legend-selected plot (plot 0 when nothing is
 * selected). Each plot's rate comes from its OWN reply, so plots of different
 * trend rates on one graph must not share plot 0's rate.
 *
 * Falls back to the spacing of the plot's own samples when its reply carried no
 * rate, then to any other plot's rate — better a neighbour's cadence than the
 * fixed default the caller would otherwise use.
 */
export function plotSampleRateSecs(plots: PlotSampleInfo[], plotIndex: number): number | null {
  const order = [plotIndex, ...plots.map((_p, i) => i)];
  for (const i of order) {
    const p = plots[i];
    if (!p) continue;
    const reported = positiveRate(p.sampleRate);
    if (reported != null) return reported;
    const estimated = positiveRate(estimateSampleRateSecs(p.timestamps));
    if (estimated != null) return estimated;
  }
  return null;
}

/**
 * Index of the plot closest to the cursor, mirroring the C++
 * `closest_plot_to_cursor_index` that `get_values_from_time_graph_coords`
 * reports (graph_dialog.cpp:12407): of each plot's sample nearest the cursor
 * time, the one whose value plots nearest the cursor's y pixel.
 *
 * `toPixel` converts a plot value to a y pixel in the same space as `cursorPx`
 * (each plot can be on its own scale in normalize mode); it returns null when
 * the plot can't be measured. Returns null when no plot can be.
 */
export function closestPlotToCursor(
  plots: PlotSampleInfo[],
  cursorSecs: number,
  toPixel: (plotIndex: number, value: number) => number | null,
  cursorPx: number,
): number | null {
  let best: number | null = null;
  let bestDist = Infinity;
  for (let i = 0; i < plots.length; i++) {
    const p = plots[i];
    if (p.visible === false) continue;
    const idx = nearestSampleIndex(p.timestamps, cursorSecs);
    if (idx == null) continue;
    const v = p.values?.[idx];
    if (v == null || !Number.isFinite(v)) continue;
    const px = toPixel(i, v);
    if (px == null || !Number.isFinite(px)) continue;
    const dist = Math.abs(px - cursorPx);
    if (dist < bestDist) {
      best = i;
      bestDist = dist;
    }
  }
  return best;
}
